import React, { useMemo } from "react";
import {
  buildWorker360Metrics,
  normalizeWorker360Value,
} from "../utils/worker360Metrics";

function InsightCard({
  title,
  description,
  tone = "neutral",
}) {
  const tones = {
    positive: {
      color: "#166534",
      background: "#f0fdf4",
      border: "#bbf7d0",
    },
    warning: {
      color: "#92400e",
      background: "#fffbeb",
      border: "#fde68a",
    },
    risk: {
      color: "#991b1b",
      background: "#fef2f2",
      border: "#fecaca",
    },
    neutral: {
      color: "#1e40af",
      background: "#eff6ff",
      border: "#bfdbfe",
    },
  };

  const style = tones[tone] || tones.neutral;

  return (
    <div
      style={{
        padding: 14,
        borderRadius: 14,
        color: style.color,
        background: style.background,
        border: `1px solid ${style.border}`,
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 950,
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: 5,
          fontSize: 12,
          fontWeight: 700,
          lineHeight: 1.5,
        }}
      >
        {description}
      </div>
    </div>
  );
}

export default function Worker360AIInsights({
  worker,
  shifts = [],
}) {

  const analysis = useMemo(() => {
const shiftMetrics = buildWorker360Metrics(
  worker,
  shifts
);

const {
  completedShifts: completed,
  noShowShifts: noShows,
  workerCancelledShifts: workerCancellations,
  lateArrivalShifts: lateArrivals,
  totalHours,
  attendance,
  facilitiesWorked,
} = shiftMetrics;

const complianceStatus =
  normalizeWorker360Value(
    worker?.complianceStatus
  );

    const operationalStatus =
  normalizeWorker360Value(
    worker?.workerOperationalState ||
      worker?.workerStatus ||
      worker?.status
  );

    const ready =
      ["ready", "active", "ready_for_shifts"].includes(
        operationalStatus
      ) && complianceStatus === "approved";

    const insights = [];
    const actions = [];

    if (ready) {
      insights.push({
        title: "Operationally Ready",
        description:
          "The worker is active, compliant, and eligible for scheduling.",
        tone: "positive",
      });
    } else {
      insights.push({
        title: "Readiness Review Required",
        description:
          "The worker’s operational or compliance status should be reviewed before assignment.",
        tone: "warning",
      });

      actions.push("Review operational and compliance status.");
    }

    if (attendance !== null && attendance >= 95) {
      insights.push({
        title: "Strong Attendance",
        description: `${attendance}% attendance across recorded completed and no-show outcomes.`,
        tone: "positive",
      });
    } else if (attendance !== null && attendance < 90) {
      insights.push({
        title: "Attendance Risk",
        description: `${attendance}% attendance requires review before priority assignments.`,
        tone: "risk",
      });

      actions.push("Review recent attendance outcomes.");
    } else {
      insights.push({
        title: "Limited Attendance History",
        description:
          "More completed shift outcomes are needed for a dependable attendance assessment.",
        tone: "neutral",
      });
    }

    if (noShows.length > 0) {
      insights.push({
        title: "No-Show History",
        description: `${noShows.length} no-show ${
          noShows.length === 1 ? "event is" : "events are"
        } recorded.`,
        tone: "risk",
      });

      actions.push("Review no-show history before assignment.");
    }

    if (workerCancellations.length > 0) {
      insights.push({
        title: "Worker Cancellations",
        description: `${workerCancellations.length} worker-initiated ${
          workerCancellations.length === 1
            ? "cancellation is"
            : "cancellations are"
        } recorded.`,
        tone: "warning",
      });

      actions.push("Discuss cancellation patterns with the worker.");
    }

    if (lateArrivals.length > 0) {
      insights.push({
        title: "Late Arrival Pattern",
        description: `${lateArrivals.length} arrival ${
          lateArrivals.length === 1 ? "was" : "were"
        } more than 15 minutes late.`,
        tone: "warning",
      });

      actions.push("Confirm transportation and arrival readiness.");
    }

    if (
      completed.length >= 2 &&
      noShows.length === 0 &&
      workerCancellations.length === 0
    ) {
      insights.push({
        title: "Recommended for Assignment",
        description:
          "Recorded history supports continued assignment, subject to role, credential, availability, and overlap checks.",
        tone: "positive",
      });
    }

    if (completed.length === 0) {
      actions.push(
        "Build performance history through an appropriate initial assignment."
      );
    }

    if (actions.length === 0) {
      actions.push(
        "Continue normal scheduling and routine compliance monitoring."
      );
    }

    return {
      completedCount: completed.length,
      totalHours,
      attendance,
      facilityCount: facilitiesWorked,
      insights,
      actions,
    };
  }, [worker, shifts]);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div
        style={{
          padding: 17,
          borderRadius: 17,
          color: "#ffffff",
          background:
            "linear-gradient(135deg, #312e81 0%, #7c3aed 100%)",
        }}
      >
        <div
          style={{
            fontSize: 20,
            fontWeight: 950,
          }}
        >
          AI Workforce Insights
        </div>

        <div
          style={{
            marginTop: 5,
            color: "#ede9fe",
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.5,
          }}
        >
          Evidence-based operational guidance from the worker’s current records.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 9,
        }}
      >
        <InsightCard
          title="Completed Shifts"
          description={analysis.completedCount}
          tone="neutral"
        />

        <InsightCard
          title="Hours Worked"
          description={analysis.totalHours.toFixed(1)}
          tone="neutral"
        />

        <InsightCard
          title="Attendance"
          description={
            analysis.attendance !== null
              ? `${analysis.attendance}%`
              : "Insufficient history"
          }
          tone={
            analysis.attendance === null
              ? "neutral"
              : analysis.attendance >= 95
              ? "positive"
              : "warning"
          }
        />

        <InsightCard
          title="Facilities Worked"
          description={analysis.facilityCount}
          tone="neutral"
        />
      </div>

      <div style={{ display: "grid", gap: 9 }}>
        <div
          style={{
            color: "#0f172a",
            fontSize: 15,
            fontWeight: 950,
          }}
        >
          Operational Assessment
        </div>

        {analysis.insights.map((insight, index) => (
          <InsightCard
            key={`${insight.title}-${index}`}
            title={insight.title}
            description={insight.description}
            tone={insight.tone}
          />
        ))}
      </div>

      <div
        style={{
          padding: 14,
          borderRadius: 15,
          border: "1px solid #ddd6fe",
          background: "#faf5ff",
        }}
      >
        <div
          style={{
            color: "#6d28d9",
            fontSize: 14,
            fontWeight: 950,
          }}
        >
          Recommended Actions
        </div>

        <div
          style={{
            marginTop: 9,
            display: "grid",
            gap: 7,
            color: "#475569",
            fontSize: 12,
            fontWeight: 750,
            lineHeight: 1.5,
          }}
        >
          {analysis.actions.map((action, index) => (
            <div key={`${action}-${index}`}>
              • {action}
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          color: "#64748b",
          fontSize: 10,
          fontWeight: 700,
          lineHeight: 1.45,
        }}
      >
        These insights are decision support only. Administrators must verify credentials, availability, facility requirements, and scheduling conflicts before acting.
      </div>
    </div>
  );
}